// AdminBooksPage.tsx
// Tela de moderação: admin visualiza, aprova ou rejeita livros pendentes

import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle, XCircle, RefreshCw, FileText, Eye, ChevronDown, ChevronUp } from "react-feather";
import { BookOpen } from "lucide-react";
import "./style.css";
import { useAuth, useMessage } from "../../../../global-components/src";
import { READBOOKS_API_URL } from "../../../../global-components/src/utils/vars";
import { errorMessage } from "../../../../global-components/src/utils/text";
import Spinner from "../../../../global-components/src/components/spinner/spiner";
import { getBookImage } from "../../../../global-components/src/utils/book";

interface CatalogBookAdmin {
    id: number;
    uuid: string;
    title: string;
    author: string | null;
    description: string | null;
    cover_url: string | null;
    type: "google_books" | "own_work";
    price: number;
    currency: string;
    file_size_bytes: number;
    total_pages: number | null;
    uploaded_by: number;
    uploader?: { id: number; name: string; email: string };
    created_at: string;
    status: "pending" | "approved" | "rejected";
    rejection_reason: string | null;
}

const fmt = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtCurrency = (v: number, currency = "BRL") =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency }).format(v);

const fmtDate = (iso: string) =>
    new Date(iso).toLocaleString("pt-BR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit"
    });

export default function AdminBooksPage() {
    const { token } = useAuth();
    const { setMessage } = useMessage();

    const [books, setBooks] = useState<CatalogBookAdmin[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
    const [rejectReason, setRejectReason] = useState<Record<number, string>>({});
    const [showRejectForm, setShowRejectForm] = useState<number | null>(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${READBOOKS_API_URL}/admin/catalog/pending`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setBooks(res.data.books);
        } catch (err: any) {
            setMessage(errorMessage(err), "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const review = async (uuid: string, id: number, action: "approve" | "reject") => {
        const reason = rejectReason[id]?.trim();
        if (action === "reject" && !reason) {
            setMessage("Informe o motivo da rejeição.", "error");
            return;
        }
        setActionLoadingId(id);
        try {
            await axios.post(
                `${READBOOKS_API_URL}/admin/catalog/${uuid}/review`,
                { action, rejection_reason: action === "reject" ? reason : undefined },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setMessage(action === "approve" ? "Livro aprovado!" : "Livro rejeitado.", "success");
            setShowRejectForm(null);
            setBooks(prev => prev.filter(b => b.id !== id));
        } catch (err: any) {
            setMessage(errorMessage(err), "error");
        } finally {
            setActionLoadingId(null);
        }
    };

    const toggle = (id: number) =>
        setExpandedId(prev => prev === id ? null : id);

    return (
        <div className="ab-wrapper margin-top-menu container">
            <div className="ab-header">
                <div>
                    <h1>Moderação de livros</h1>
                    <p>{books.length} livro{books.length !== 1 ? "s" : ""} aguardando revisão</p>
                </div>
                <button className="btn btn-outline-secondary ab-refresh" onClick={load}>
                    <RefreshCw size={14} /> Atualizar
                </button>
            </div>

            {loading ? (
                <div className="ab-loading"><Spinner size="36px" /></div>
            ) : books.length === 0 ? (
                <div className="ab-empty">
                    <CheckCircle size={44} />
                    <p>Nenhum livro pendente de aprovação</p>
                </div>
            ) : (
                <div className="ab-list">
                    {books.map(book => {
                        const expanded = expandedId === book.id;
                        const isActing = actionLoadingId === book.id;

                        return (
                            <div key={book.id} className={`ab-card ${expanded ? "expanded" : ""}`}>

                                {/* ── Linha principal ── */}
                                <div className="ab-card-main" onClick={() => toggle(book.id)}>
                                    <div className="ab-cover-wrap">
                                        {book.cover_url ? (
                                            <img
                                                src={book.cover_url
                                                    ? getBookImage(book.cover_url)
                                                    : "/img/book_transparent.png"}
                                                alt={book.title}
                                                className="ab-cover"
                                                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                                            />
                                        ) : (
                                            <div className="ab-cover-placeholder">
                                                <BookOpen size={20} />
                                            </div>
                                        )}
                                    </div>

                                    <div className="ab-card-info">
                                        <span className="ab-title">{book.title}</span>
                                        <span className="ab-author">{book.author ?? "Autor não informado"}</span>
                                        <div className="ab-meta-row">
                                            <span className="ab-badge ab-badge--type">
                                                {book.type === "own_work" ? "Obra própria" : "Google Books"}
                                            </span>
                                            {book.price > 0 ? (
                                                <span className="ab-badge ab-badge--paid">
                                                    {fmtCurrency(book.price, book.currency)}
                                                </span>
                                            ) : (
                                                <span className="ab-badge ab-badge--free">Gratuito</span>
                                            )}
                                            {book.file_size_bytes > 0 && (
                                                <span className="ab-badge ab-badge--size">
                                                    <FileText size={11} /> {fmt(book.file_size_bytes)}
                                                </span>
                                            )}
                                        </div>
                                        <span className="ab-date">Enviado em {fmtDate(book.created_at)}</span>
                                    </div>

                                    <div className="ab-card-toggle">
                                        {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                    </div>
                                </div>

                                {/* ── Detalhe expandido ── */}
                                {expanded && (
                                    <div className="ab-card-detail">
                                        {book.description && (
                                            <div className="ab-detail-section">
                                                <span className="ab-detail-label">Descrição</span>
                                                <p className="ab-description">{book.description}</p>
                                            </div>
                                        )}

                                        <div className="ab-detail-row">
                                            {book.total_pages && (
                                                <div className="ab-detail-item">
                                                    <span className="ab-detail-label">Páginas</span>
                                                    <span>{book.total_pages}</span>
                                                </div>
                                            )}
                                            <div className="ab-detail-item">
                                                <span className="ab-detail-label">ID do uploader</span>
                                                <span>#{book.uploaded_by}</span>
                                            </div>
                                        </div>

                                        {/* Botões de ação */}
                                        {showRejectForm !== book.id ? (
                                            <div className="ab-actions">
                                                {book.status != "approved" && (
                                                    <button
                                                        className="btn btn-success ab-action-btn"
                                                        disabled={isActing}
                                                        onClick={() => review(book.uuid, book.id, "approve")}
                                                    >
                                                        {isActing ? <Spinner size="16px" /> : <><CheckCircle size={15} /> Aprovar</>}
                                                    </button>
                                                )}
                                                {book.status != "rejected" && (
                                                    <button
                                                        className="btn btn-outline-danger ab-action-btn"
                                                        disabled={isActing}
                                                        onClick={() => setShowRejectForm(book.id)}
                                                    >
                                                        <XCircle size={15} /> Rejeitar
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="ab-reject-form">
                                                <label className="ab-detail-label">Motivo da rejeição *</label>
                                                <textarea
                                                    className="form-control"
                                                    rows={3}
                                                    placeholder="Explique o motivo para o autor..."
                                                    value={rejectReason[book.id] ?? ""}
                                                    onChange={e => setRejectReason(prev => ({ ...prev, [book.id]: e.target.value }))}
                                                />
                                                <div className="ab-reject-btns">
                                                    <button
                                                        className="btn btn-danger ab-action-btn"
                                                        disabled={isActing}
                                                        onClick={() => review(book.uuid, book.id, "reject")}
                                                    >
                                                        {isActing ? <Spinner size="16px" /> : <><XCircle size={15} /> Confirmar rejeição</>}
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-secondary ab-action-btn"
                                                        onClick={() => setShowRejectForm(null)}
                                                    >
                                                        Cancelar
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}